import { forwardRef, useEffect } from 'react'
import DatePicker from 'react-datepicker'
import { ar, enUS } from 'date-fns/locale'
import { useTranslation } from '../../core/hooks/useTranslation'
import 'react-datepicker/dist/react-datepicker.css'

interface DateInputProps {
  label?: string
  error?: string
  helperText?: string
  [key: string]: any // Allow all DatePicker props
}

const DateInput = forwardRef<DatePicker, DateInputProps>(
  ({ label, error, helperText, className = '', ...props }, ref) => {
    const { isRTL, currentLanguage } = useTranslation()
    const locale = currentLanguage === 'ar' ? ar : enUS
    const dateFormat = currentLanguage === 'ar' ? 'dd/MM/yyyy' : 'MM/dd/yyyy'

    // تطبيق RTL style للـ close icon مباشرة
    useEffect(() => {
      const styleId = 'datepicker-close-icon-rtl'
      let styleElement = document.getElementById(styleId) as HTMLStyleElement | null

      if (!styleElement) {
        styleElement = document.createElement('style')
        styleElement.id = styleId
        document.head.appendChild(styleElement)
      }

      if (isRTL) {
        styleElement.textContent = `
          .react-datepicker__close-icon {
            left: 0.5rem !important;
            right: auto !important;
          }
        `
      } else {
        styleElement.textContent = `
          .react-datepicker__close-icon {
            right: 0.5rem !important;
            left: auto !important;
          }
        `
      }

      return () => {
        // لا نحذف الـ style element لأنه قد يُستخدم في أماكن أخرى
      }
    }, [isRTL])

    return (
      <div className="space-y-2">
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        
        <div className="relative" dir={isRTL ? 'rtl' : 'ltr'}>
          <DatePicker
            ref={ref}
            locale={locale}
            dateFormat={dateFormat}
            className={`input ${error ? 'input-error' : ''} ${className}`}
            wrapperClassName={`w-full ${isRTL ? 'rtl' : ''}`}
            calendarClassName={isRTL ? 'rtl' : ''}
            popperClassName={`${isRTL ? 'rtl' : ''} react-datepicker-popper-modal`}
            popperModifiers={[
              {
                name: 'offset',
                options: {
                  offset: [0, 8],
                },
              } as any,
            ]}
            withPortal
            {...props}
          />
        </div>
        
        {error && (
          <div className="flex items-center space-x-1 rtl:space-x-reverse">
            <p className="text-sm text-red-600 font-medium">{error}</p>
          </div>
        )}
        
        {helperText && !error && (
          <p className="text-sm text-gray-500">{helperText}</p>
        )}
      </div>
    )
  }
)

DateInput.displayName = 'DateInput'

export default DateInput

